// src/pages/creator-panel/LivePhoneCall/PhoneProviderConfig.js
// Voice-channel provider configuration page. Uses the shared
// /api/sms/service/configure endpoint with channels=['phone'] (or
// ['sms','phone'] when reusing an existing SMS Twilio/Vonage credential).
// AWS SNS is intentionally hidden — it has no voice product.
import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    Box, Typography, Alert, Button, TextField, CircularProgress, Card, CardContent,
    Select, MenuItem, FormControl, InputLabel, Chip, FormControlLabel, Checkbox,
} from '@mui/material';
import { Save as SaveIcon, Phone as PhoneIcon } from '@mui/icons-material';

// Voice-capable providers only — AWS SNS removed.
const PROVIDERS = [
    { key: 'TWILIO', label: 'Twilio Voice', fields: [
        { name: 'account_sid', label: 'Account SID' },
        { name: 'auth_token', label: 'Auth Token', secret: true },
        { name: 'from_number', label: 'SMS From Number (E.164)' },
    ], phoneFields: [
        { name: 'voice_from_number', label: 'Voice From Number (E.164)' },
        { name: 'webhook_secret', label: 'Webhook secret (optional)', secret: true },
    ]},
    { key: 'VONAGE', label: 'Vonage Voice', fields: [
        { name: 'api_key', label: 'API Key' },
        { name: 'api_secret', label: 'API Secret', secret: true },
        { name: 'from_number', label: 'SMS From Number (E.164)' },
    ], phoneFields: [
        { name: 'application_id', label: 'Vonage Application ID' },
        { name: 'private_key_pem', label: 'Application private key (PEM)', secret: true, multiline: true },
        { name: 'public_key_pem', label: 'Application public key (PEM, for inbound JWT verify)', multiline: true },
    ]},
    { key: 'MESSAGEBIRD', label: 'MessageBird Voice', fields: [
        { name: 'api_key', label: 'API Key', secret: true },
        { name: 'originator', label: 'SMS Originator' },
    ], phoneFields: [
        { name: 'signing_key', label: 'Webhook signing key', secret: true },
    ]},
];

export default function PhoneProviderConfig() {
    const [canView, setCanView] = useState(false);
    const [canModify, setCanModify] = useState(false);
    const [checkingPerm, setCheckingPerm] = useState(true);

    const [provider, setProvider] = useState('TWILIO');
    const [smsConfig, setSmsConfig] = useState({});
    const [phoneConfig, setPhoneConfig] = useState({});
    const [alsoEnableSms, setAlsoEnableSms] = useState(false);
    const [existing, setExisting] = useState([]);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        let active = true;
        (async () => {
            const v = await hasPermission('live-phone-call', 'view');
            const a = await hasPermission('live-phone-call', 'add');
            const e = await hasPermission('live-phone-call', 'edit');
            if (!active) return;
            setCanView(v); setCanModify(a || e); setCheckingPerm(false);
        })();
        return () => { active = false; };
    }, []);

    const loadConfigs = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/sms/service/configs', { params: { channel: 'phone' } });
            setExisting(res?.data?.data || []);
        } catch (e) { /* tolerate */ }
    }, []);

    useEffect(() => { if (!checkingPerm && canView) loadConfigs(); }, [checkingPerm, canView, loadConfigs]);

    const currentDef = PROVIDERS.find(p => p.key === provider);

    const handleSave = async () => {
        setSaving(true);
        try {
            const channels = alsoEnableSms ? ['sms', 'phone'] : ['phone'];
            await axiosInstance.post('/sms/service/configure', {
                service_type: provider,
                config: smsConfig,
                phone_config: phoneConfig,
                channels,
            });
            setMessage({ kind: 'success', text: `${currentDef.label} saved.` });
            loadConfigs();
        } catch (e) {
            const msg = e?.response?.data?.message || 'Save failed.';
            setMessage({ kind: 'error', text: msg });
        } finally {
            setSaving(false);
        }
    };

    if (checkingPerm) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
    if (!canView) return <Alert severity="warning" sx={{ m: 3 }}>You don't have permission to manage phone providers.</Alert>;

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1000, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <PhoneIcon color="primary" />
                <Typography variant="h5">Phone Providers</Typography>
            </Box>

            {message && (
                <Alert severity={message.kind} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>
            )}

            {existing.length > 0 && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 1 }}>Configured Providers</Typography>
                        {existing.map(p => (
                            <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography sx={{ flex: 1 }}>{p.service_type}</Typography>
                                {(p.channels || []).map(c => <Chip key={c} size="small" label={c.toUpperCase()} />)}
                                {p.is_active && <Chip size="small" color="success" label="ACTIVE" />}
                            </Box>
                        ))}
                    </CardContent>
                </Card>
            )}

            <Card>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Add / Update Provider</Typography>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Provider</InputLabel>
                        <Select value={provider} label="Provider" onChange={(e) => setProvider(e.target.value)} disabled={!canModify}>
                            {PROVIDERS.map(p => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
                        </Select>
                    </FormControl>

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Provider Credentials (shared with SMS if enabled)</Typography>
                    {currentDef.fields.map(f => (
                        <TextField
                            key={f.name}
                            label={f.label}
                            type={f.secret ? 'password' : 'text'}
                            value={smsConfig[f.name] || ''}
                            onChange={(e) => setSmsConfig({ ...smsConfig, [f.name]: e.target.value })}
                            fullWidth sx={{ mb: 2 }}
                            disabled={!canModify}
                        />
                    ))}

                    <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>Voice-specific Configuration</Typography>
                    {currentDef.phoneFields.map(f => (
                        <TextField
                            key={f.name}
                            label={f.label}
                            type={f.secret && !f.multiline ? 'password' : 'text'}
                            value={phoneConfig[f.name] || ''}
                            onChange={(e) => setPhoneConfig({ ...phoneConfig, [f.name]: e.target.value })}
                            fullWidth sx={{ mb: 2 }}
                            multiline={!!f.multiline}
                            minRows={f.multiline ? 4 : 1}
                            disabled={!canModify}
                        />
                    ))}

                    <FormControlLabel
                        control={<Checkbox checked={alsoEnableSms} onChange={(e) => setAlsoEnableSms(e.target.checked)} disabled={!canModify} />}
                        label="Also enable this provider for SMS (same credentials)"
                        sx={{ mb: 2 }}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={!canModify || saving}>
                            {saving ? 'Saving…' : 'Save'}
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}
